import React, { useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  Grid,
  IconButton,
  Container,
  Divider,
} from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import robio from "../assets/robio.png";

// Icons
import ScienceIcon from "@mui/icons-material/Science";
import ArticleIcon from "@mui/icons-material/Article";
import BiotechIcon from "@mui/icons-material/Biotech";
import SearchIcon from "@mui/icons-material/Search";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/Email";

import rohithImg from "../assets/Authors/rohith.png";
import arunImg from "../assets/Authors/arun.png";
import hariImg from "../assets/Authors/hari.jpeg";

import { auth } from "../firebase";

const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.3,
    },
  },
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

function LandingPage() {
  const navigate = useNavigate();
  const isLoggedIn = !!auth.currentUser;
  const scrollRef = useRef(null);

  const features = [
    {
      name: "Discover",
      route: "/biothread/discover",
      desc: "Instantly uncover biologically supported outcomes for compounds using PubMed evidence.",
      icon: <SearchIcon sx={{ fontSize: 40 }} />,
      color: "#3b82f6",
    },
    {
      name: "RELIO",
      route: "/biothread/litmap",
      desc: "Map compound targets and pathways using biomedical literature.",
      icon: <ScienceIcon sx={{ fontSize: 40 }} />,
      color: "#8b5cf6",
    },
    {
      name: "Nucleo Read",
      route: "/seqintel/nucleoread",
      desc: "Decode nucleotide sequences into genomic insights.",
      icon: <BiotechIcon sx={{ fontSize: 40 }} />,
      color: "#ec4899",
    },
    {
      name: "Mutalytics",
      route: "/seqintel/mutalytics",
      desc: "Evaluate protein mutations efficiently before simulations.",
      icon: <BiotechIcon sx={{ fontSize: 40 }} />,
      color: "#f59e0b",
    },
    {
      name: "Article Miner",
      route: "/article",
      desc: "Organize and prioritize scientific publications.",
      icon: <ArticleIcon sx={{ fontSize: 40 }} />,
      color: "#10b981",
    },
  ];

  const authors = [
    {
      name: "Rohith S",
      position: "Founder",
      image: rohithImg,
      email: "saburohith07@gmail.com",
      linkedin: "https://www.linkedin.com/in/rohith-s-56a748222/",
    },
    {
      name: "Arun Kumar N",
      position: "Co-Founder",
      image: arunImg,
      email: "arunkumarn299@gmail.com",
      linkedin: "https://www.linkedin.com/in/arun-kumar-8570801b6/",
    },
    {
      name: "Haridharshini J",
      position: "Technical Lead",
      image: hariImg,
      email: "haridharshinijayaraj@gmail.com",
      linkedin: "https://www.linkedin.com/in/haridharshini-jayaraj-45a704306/",
    },
  ];

  const scroll = (direction) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({
        left: direction === "left" ? -320 : 320,
        behavior: "smooth",
      });
    }
  };

  const handleScrollOrLogin = () => {
    if (isLoggedIn) {
      const section = document.getElementById("features-section");
      section?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/login");
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)",
      }}
    >
      {/* HERO SECTION */}
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: "center",
            justifyContent: "space-between",
            py: { xs: 8, md: 12 },
            minHeight: { xs: "auto", md: "70vh" },
          }}
        >
          <Box
            component={motion.div}
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
            sx={{ flex: 1 }}
          >
            <Typography
              variant="h2"
              fontWeight={800}
              sx={{
                background: "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                lineHeight: 1.2,
              }}
            >
              Powering Smarter Biological Discovery
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "1rem", md: "1.125rem" },
                lineHeight: 1.6,
                mb: 4,
                maxWidth: "90%",
              }}
            >
              Turning biological complexity into actionable research
              intelligence with AI-powered tools.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ maxWidth: "400px" }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={handleScrollOrLogin}
                sx={{
                  background:
                    "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  "&:hover": {
                    boxShadow: "0 10px 25px rgba(59, 130, 246, 0.3)",
                  },
                }}
              >
                Get Started
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleScrollOrLogin}
                sx={{
                  textTransform: "none",
                  fontSize: "1rem",
                  fontWeight: 600,
                  py: 1.5,
                  px: 3,
                  borderRadius: 2,
                  borderWidth: 2,
                  "&:hover": {
                    borderWidth: 2,
                    background: "rgba(59, 130, 246, 0.05)",
                  },
                }}
              >
                Learn More
              </Button>
            </Stack>
          </Box>

          <Box
            component={motion.div}
            animate={{ y: [0, -15, 0] }}
            transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: { xs: "100%", md: 400 },
                height: { xs: "auto", md: 400 },
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: { xs: "300px", md: "350px" },
                  height: { xs: "300px", md: "350px" },
                  background:
                    "radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)",
                  borderRadius: "50%",
                  animation: "pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite",
                  "@keyframes pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.5 },
                  },
                }}
              />
              <img
                src={robio}
                alt="Robio"
                style={{
                  maxWidth: "90%",
                  height: "auto",
                  position: "relative",
                  zIndex: 1,
                }}
              />
            </Box>
          </Box>
        </Box>
      </Container>

      {/* FEATURES SECTION */}
      <Box
        id="features-section"
        sx={{ py: { xs: 8, md: 12 }, background: "#ffffff" }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                mb: 2,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
              }}
            >
              Explore Our Tools
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: "1.125rem",
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Comprehensive solutions for biological research and analysis
            </Typography>
          </Box>

          {/* Features Carousel */}
          <Box
            component={motion.div}
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            sx={{ position: "relative" }}
          >
            {/* Left Arrow */}
            <IconButton
              onClick={() => scroll("left")}
              sx={{
                position: "absolute",
                left: { xs: 0, md: -20 },
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                background: "white",
                boxShadow: 2,
                "&:hover": {
                  background: "#f0f4f8",
                  boxShadow: 4,
                },
              }}
            >
              <ArrowBackIosNewIcon />
            </IconButton>

            {/* Carousel Content */}
            <Box
              ref={scrollRef}
              sx={{
                display: "flex",
                overflowX: "auto",
                gap: 3,
                pb: 2,
                scrollBehavior: "smooth",
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-track": {
                  background: "#f0f4f8",
                  borderRadius: 10,
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#3b82f6",
                  borderRadius: 10,
                  "&:hover": {
                    background: "#2563eb",
                  },
                },
              }}
            >
              {features.map((f, i) => (
                <Box
                  key={i}
                  component={motion.div}
                  variants={scaleIn}
                  onClick={() => navigate(f.route)}
                  sx={{
                    minWidth: { xs: 280, md: 320 },
                    p: 4,
                    background: "white",
                    border: "1px solid #e5e7eb",
                    borderRadius: 3,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    transition: "all 0.3s ease",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 20px 25px rgba(0, 0, 0, 0.1)",
                      borderColor: f.color,
                    },
                  }}
                >
                  <Stack spacing={3}>
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        background: `${f.color}15`,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: f.color,
                      }}
                    >
                      {f.icon}
                    </Box>

                    <Box>
                      <Typography
                        fontWeight={700}
                        sx={{
                          fontSize: "1.25rem",
                          mb: 1,
                          color: "#1f2937",
                        }}
                      >
                        {f.name}
                      </Typography>
                      <Typography
                        sx={{
                          color: "text.secondary",
                          fontSize: "0.95rem",
                          lineHeight: 1.6,
                        }}
                      >
                        {f.desc}
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      size="small"
                      sx={{
                        background: f.color,
                        textTransform: "none",
                        fontWeight: 600,
                        mt: "auto",
                        alignSelf: "flex-start",
                        "&:hover": {
                          background: f.color,
                          opacity: 0.9,
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(f.route);
                      }}
                    >
                      Try Feature →
                    </Button>
                  </Stack>
                </Box>
              ))}
            </Box>

            {/* Right Arrow */}
            <IconButton
              onClick={() => scroll("right")}
              sx={{
                position: "absolute",
                right: { xs: 0, md: -20 },
                top: "50%",
                transform: "translateY(-50%)",
                zIndex: 10,
                background: "white",
                boxShadow: 2,
                "&:hover": {
                  background: "#f0f4f8",
                  boxShadow: 4,
                },
              }}
            >
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* AUTHORS SECTION */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(135deg, #f8fafc 0%, #f0f4f8 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: 8 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                mb: 2,
                fontSize: { xs: "1.75rem", md: "2.5rem" },
              }}
            >
              Meet the Authors
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: "1.125rem",
              }}
            >
              Passionate about advancing biological research
            </Typography>
          </Box>

          {/* Centered authors grid */}
          <Grid container spacing={4} justifyContent="center">
            {authors.map((a, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Box
                  component={motion.div}
                  variants={scaleIn}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  sx={{
                    textAlign: "center",
                    p: 4,
                    background: "white",
                    borderRadius: 3,
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
                    border: "1px solid #e5e7eb",
                    transition: "all 0.3s ease",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center", // Center horizontally
                    "&:hover": {
                      transform: "translateY(-8px)",
                      boxShadow: "0 20px 25px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      mb: 3,
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 120,
                        height: 120,
                        borderRadius: "50%",
                        overflow: "hidden",
                        border: "4px solid #3b82f6",
                        background:
                          "linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)",
                      }}
                    >
                      <img
                        src={a.image}
                        alt={a.name}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </Box>
                  </Box>

                  <Typography
                    fontWeight={700}
                    sx={{
                      fontSize: "1.25rem",
                      mb: 0.5,
                      color: "#1f2937",
                    }}
                  >
                    {a.name}
                  </Typography>

                  <Typography
                    sx={{
                      color: "#3b82f6",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                      mb: 2,
                    }}
                  >
                    {a.position}
                  </Typography>

                  <Divider sx={{ my: 2 }} />

                  <Stack
                    direction="row"
                    justifyContent="center"
                    spacing={2}
                    sx={{ mt: "auto" }}
                  >
                    <IconButton
                      href={`mailto:${a.email}`}
                      sx={{
                        color: "#3b82f6",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "rgba(59, 130, 246, 0.1)",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <EmailIcon />
                    </IconButton>
                    <IconButton
                      href={a.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#3b82f6",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          background: "rgba(59, 130, 246, 0.1)",
                          transform: "scale(1.1)",
                        },
                      }}
                    >
                      <LinkedInIcon />
                    </IconButton>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
    </Box>
  );
}

export default LandingPage;
